import React from "react";
import {
  List,
  TextField,
  Datagrid,
  NumberField,
  DateField,
  useRecordContext,
  TextInput,
  FunctionField,
  useNotify,
  SelectInput,
  ReferenceInput,
  ReferenceField,
  SimpleList,
  TopToolbar,
  CreateButton,
} from "react-admin";
import Box from "@mui/material/Box";
import ChangeStatusButton from "./ChangeStatusButton";
import { TransactionStatusChoices } from "utils/choices";
import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";
import permissions from "app/permissions";
import { Typography } from "@mui/material";
import { getFile } from "server/components/DownloadFile";
import { BANK_TRANSFERS, MOBILE_BREAKPOINT } from "utils/constants";
import { useMediaQuery } from "@mui/material";
import { EmptyList } from "server/components";
import { settings } from "app";
import { CashflowExporter } from "../XlsxExporter";
import { ExportXlsButton } from "features/ExportXlsxButton";
import { paymentRequests } from "api/paymentApi";
import { MobileListRow } from "features/MobileListRow";
import { getDisplayName } from "utils/helpers";

const postFilters = [
  <TextInput
    label="Основание за плащане"
    source="reason"
    style={{ width: 300 }}
    alwaysOn
  />,
  <TextInput
    label="Име"
    source="firstName"
    style={{ width: 230 }}
    alwaysOn
  />,
  <TextInput
    label="Фамилия"
    source="lastName"
    style={{ width: 230 }}
    alwaysOn
  />,
  <SelectInput
    source="status"
    label="Статус"
    choices={TransactionStatusChoices}
    emptyText="Всички"
    alwaysOn
  />,
  <TextInput
    label="Описание"
    source="description"
    alwaysOn
  />,
  <ReferenceInput
    source="eshopId"
    reference={paymentRequests.getEshopsSelect()}
    filter={{ activeOnly: true }}
    label="Е-магазин"
    alwaysOn
  >
    <SelectInput
      label="Е-магазин"
      emptyText="Всички"
    />
  </ReferenceInput>,
];

const ListBankTransfers = () => {
  const notify = useNotify();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const StatusField = () => {
    const record = useRecordContext();

    const status = TransactionStatusChoices.find(choice => choice.id === record.status);
    return <span>{status ? status.name : "Неизвестен статус"}</span>;
  };

  const ListActions = hasCreate => {
    return (
      <TopToolbar>
        {hasCreate && <CreateButton />}
        <ExportXlsButton exporter={CashflowExporter(BANK_TRANSFERS)} />
      </TopToolbar>
    );
  };

  return (
    <List
      actions={<ListActions hasCreate={true} />}
      title="Банкови трансфери"
      filters={postFilters}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          overflowY: "auto",
        }}
      >
        {isMobile ? (
          <SimpleList
            empty={<EmptyList />}
            linkType={false}
            sx={settings.style.mobileList}
            primaryText={record => <MobileListRow fields={[{ label: "Номер", value: record?.id }]} />}
            secondaryText={record => (
              <div>
                <MobileListRow
                  fields={[
                    { label: "Име", value: record?.userInfo?.firstName },
                    { label: "Презиме", value: record?.userInfo?.secondName },
                    { label: "Фамилия", value: record?.userInfo?.lastName },
                  ]}
                />
                <MobileListRow fields={[{ label: "Основание за плащане", value: record?.reason }]} />
                <MobileListRow fields={[{ label: "Сума", value: record?.amount }]} />
                {record?.statementDocumentId && (
                  <MobileListRow
                    fields={[
                      {
                        label: "Платежно",
                        value: (
                          <Typography
                            sx={{ textDecoration: "underline", fontSize: "14px", cursor: "pointer" }}
                            onClick={() => getFile(record.statementDocumentId, notify)}
                          >
                            Виж файл
                          </Typography>
                        ),
                      },
                    ]}
                  />
                )}
                <MobileListRow
                  fields={[
                    {
                      label: "Дата на създаване",
                      value: (
                        <DateField
                          source="createTimestamp"
                          showTime
                          locales="bg-BG"
                        />
                      ),
                    },
                  ]}
                />
                <MobileListRow
                  fields={[
                    {
                      label: "Дата на плащане",
                      value: (
                        <DateField
                          source="paymentTimestamp"
                          showTime
                          locales="bg-BG"
                        />
                      ),
                    },
                  ]}
                />
                <MobileListRow fields={[{ label: "Описание", value: record?.description }]} />
                <MobileListRow
                  fields={[
                    {
                      label: "Е-магазин",
                      value: (
                        <ReferenceField
                          source="eshopId"
                          reference={paymentRequests.getEshopsSelect()}
                        >
                          <TextField source="name" />
                        </ReferenceField>
                      ),
                    },
                  ]}
                />
                <MobileListRow fields={[{ label: "Статус", value: <StatusField /> }]} />
                {checkPermission(getPermissions(), permissions.canChangeBankTransferStatus) && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    <ChangeStatusButton />
                  </div>
                )}
              </div>
            )}
          />
        ) : (
          <Datagrid
            bulkActionButtons={false}
            rowClick={false}
            sx={settings.style.compactDatagridCells}
          >
            <NumberField
              source="id"
              label="Номер"
              sortable={false}
            />
            {/* One names column instead of three - saves width so the table fits a 14" screen. */}
            <FunctionField
              label="Имена"
              render={record => getDisplayName(record?.userInfo)}
            />
            <TextField
              source="reason"
              label="Основание за плащане"
              sortable={false}
            />
            <NumberField
              source="amount"
              label="Сума"
              sortable={false}
            />
            <FunctionField
              label={"Платежно"}
              render={record =>
                record.statementDocumentId && (
                  <Typography
                    sx={{ textDecoration: "underline", fontSize: "14px", cursor: "pointer" }}
                    onClick={() => getFile(record.statementDocumentId, notify)}
                  >{`Виж файл`}</Typography>
                )
              }
            />
            <DateField
              source="createTimestamp"
              label="Дата на създаване"
              showTime
              locales="bg-BG"
              sortable={false}
            />
            <DateField
              source="paymentTimestamp"
              label="Дата на плащане"
              showTime
              locales="bg-BG"
              sortable={false}
            />
            <TextField
              source="description"
              label="Описание"
              sortable={false}
            />
            <ReferenceField
              source="eshopId"
              reference={paymentRequests.getEshopsSelect()}
              label="Е-магазин"
              sortable={false}
            >
              <TextField
                source="name"
                label="E-магазин"
              />
            </ReferenceField>
            <StatusField label="Статус" />,
            {checkPermission(getPermissions(), permissions.canChangeBankTransferStatus) && <ChangeStatusButton />}
          </Datagrid>
        )}
      </Box>
    </List>
  );
};

export default ListBankTransfers;
